class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Elshorafa Co 2026-10-01: products, collections and suggested searches; Shopify's suggest service rejects the Arabic
    // locale (417 Unsupported buyer locale), so Arabic pages ask the default endpoint and links are mapped back to /ar.
    const ecAr = routes.predictive_search_url.indexOf('/ar/') === 0;
    const ecBase = ecAr ? routes.predictive_search_url.slice(3) : routes.predictive_search_url;
    const ecParams = `q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,collection,query&${encodeURIComponent('resources[limit]')}=5&${encodeURIComponent('resources[limit_scope]')}=each&${encodeURIComponent('resources[options][fields]')}=title,product_type,variants.title,vendor,tag&section_id=predictive-search`;
    fetch(`${ecBase}?${ecParams}`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        let resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        if (ecAr) {
          resultsMarkup = resultsMarkup.replace(/href="\/(products|collections|search)/g, 'href="/ar/$1')
            .replace('data-ec-ps="sugg">Suggestions<', 'data-ec-ps="sugg">اقتراحات<')
            .replace('data-ec-ps="cols">Collections<', 'data-ec-ps="cols">المجموعات<');
        }
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.ecFuzzyCollections();
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  // Elshorafa Co 2026-10-01: typo and synonym tolerant collection matches (English and Arabic), shown when Shopify
  // returns no collection for the term, e.g. "neklace", "earing", "سوار".
  ecFuzzyCollections() {
    const box = this.predictiveSearchResults.querySelector('#predictive-search-results');
    const input = this.querySelector('input[type="search"]');
    if (!box || !input) return;
    const ar = (document.documentElement.lang || '').indexOf('ar') === 0;
    if (ar) {
      const h = box.querySelector('#predictive-search-products');
      if (h && h.firstChild && h.firstChild.nodeType === 3) h.firstChild.nodeValue = 'المنتجات ';
      const term = box.querySelector('.predictive-search__item--term');
      if (term && term.firstChild && term.firstChild.nodeType === 3) term.firstChild.nodeValue = 'ابحثي عن "' + input.value.trim() + '" ';
    }
    if (box.querySelector('.ec-ps-cols')) return;
    const pre = ar ? '/ar' : '';
    const C = [
      ['necklaces', 'Necklaces', 'القلائد', 'necklace necklaces chain chains pendant pendants choker قلادة قلائد عقد سلسال سلسلة تعليقة'],
      ['rings', 'Rings', 'الخواتم', 'ring rings band خاتم خواتم دبلة'],
      ['earrings', 'Earrings', 'الأقراط', 'earring earrings studs stud hoops hoop حلق اقراط أقراط قرط'],
      ['bracelets', 'Bracelets', 'الأساور', 'bracelet bracelets bangle bangles cuff anklet سوار اسوارة أسوارة اساور أساور غويشة'],
      ['sets', 'Sets', 'الأطقم', 'set sets jewelry set طقم اطقم أطقم'],
      ['bundles', 'Bundles', 'الباقات', 'bundle bundles offer offers باقة باقات عرض'],
      ['uae-necklaces', 'UAE Necklaces', 'قلائد الإمارات', 'uae emirates map flag national day watan امارات الإمارات خريطة علم وطن'],
      ['islamic-necklaces', 'Islamic Necklaces', 'قلائد إسلامية', 'islamic allah ayatul kursi quran mashallah الله آية الكرسي قران قرآن ماشاءالله'],
      ['halloween-gifts', 'Halloween Gifts', 'هدايا الهالوين', 'halloween هالوين'],
      ['valentines-gifts', "Valentine's Gifts", 'هدايا عيد الحب', 'valentine valentines love heart حب عيد الحب قلب'],
      ['best-seller', 'Best sellers', 'الأكثر مبيعاً', 'best seller bestseller popular الأكثر مبيعا'],
      ['new-arrivals', 'New arrivals', 'وصل حديثاً', 'new arrivals latest جديد وصل حديثا']
    ];
    const norm = (t) => t.toLowerCase().replace(/[\u064B-\u0652\u0640]/g, '').replace(/[أإآ]/g, 'ا').replace(/ة/g, 'ه').replace(/ى/g, 'ي').trim();
    const lev = (a, b) => {
      if (Math.abs(a.length - b.length) > 2) return 9;
      const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
      for (let j = 1; j <= b.length; j++) d[0][j] = j;
      for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++)
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      return d[a.length][b.length];
    };
    const words = norm(input.value).split(/\s+/).filter((w) => w.length >= 3);
    if (!words.length) return;
    const hits = C.filter((c) => norm(c[3]).split(/\s+/).some((k) => words.some((w) => k === w || (w.length >= 4 && k.indexOf(w) === 0) || lev(w, k) <= (w.length >= 6 ? 2 : 1)))).slice(0, 3);
    if (!hits.length) return;
    const html = '<div class="ec-ps-block"><p class="ec-ps-head">' + (ar ? 'المجموعات' : 'Collections') + '</p><div class="ec-ps-cols">' +
      hits.map((c) => '<a class="ec-ps-col" href="' + pre + '/collections/' + c[0] + '"><span>' + (ar ? c[2] : c[1]) + '</span></a>').join('') + '</div></div>';
    const firstBlock = box.querySelector('.ec-ps-block');
    if (firstBlock) firstBlock.insertAdjacentHTML('afterend', html); else box.insertAdjacentHTML('afterbegin', html);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
